import api from './apiClient'

/**
 * Migração do banco de origem (`/migration`).
 *
 * A origem é uma conexão completa informada pelo usuário — servidor, porta,
 * banco, usuário e senha — e pode ser qualquer SQL Server. Por isso:
 *
 *  - **A senha viaja no corpo, nunca em query string.** É por isso que testar
 *    conexão, listar bancos e simular são `POST` mesmo sendo leitura: query
 *    string acaba em log de proxy e no histórico do navegador.
 *  - **A senha nunca é guardada aqui.** Vive no estado da tela enquanto a
 *    pessoa está nela. Só é persistida se ela pedir, em `salvarConexao`, e aí
 *    quem cifra é a API.
 *  - **A API nunca devolve a senha.** Nenhuma resposta abaixo a contém.
 *
 * A origem é aberta somente para leitura; toda escrita vai para o banco novo.
 */

/** Corpo da conexão, com os defaults que a API espera. */
const corpoDaConexao = conexao => ({
  host: (conexao.host || '').trim(),
  port: Number(conexao.port) || 1433,
  user: (conexao.user || '').trim(),
  password: conexao.password || '',
  encrypt: Boolean(conexao.encrypt),
  trustServerCertificate: Boolean(conexao.trustServerCertificate),
  ...(conexao.database ? { database: conexao.database } : {}),
})

/** `POST /migration/connections/test` — conecta e diz o que encontrou. */
export const testarConexao = async conexao => {
  const response = await api.post('/migration/connections/test', corpoDaConexao(conexao))
  return response.data
}

/** `POST /migration/connections/databases` — bancos que a conexão enxerga. */
export const listarBancos = async conexao => {
  const response = await api.post('/migration/connections/databases', {
    ...corpoDaConexao(conexao),
    database: undefined,
  })
  return response.data.items || []
}

/**
 * `POST /migration/connections/companies` — empresas e filiais daquele banco.
 *
 * O banco de origem é multiempresa. Sem esta lista, a tela só saberia oferecer
 * "migrar tudo", que traz produção de cliente nenhum tem a ver com esta
 * instalação. As contagens vêm junto porque nome de empresa sozinho não diz
 * qual é a que interessa.
 */
export const listarEmpresas = async conexao => {
  const response = await api.post('/migration/connections/companies', corpoDaConexao(conexao))
  return response.data.items || []
}

/**
 * Recorte no formato que a API espera.
 *
 * Sem empresa não vai nada: campo ausente é o que significa "o banco inteiro",
 * e mandar `null` explícito seria pedir o mesmo por um caminho que a rota não
 * documenta.
 */
const corpoDoEscopo = escopo => {
  if (!escopo?.companyId) return {}
  return {
    escopo: {
      companyId: escopo.companyId,
      ...(escopo.subCompanyIds?.length > 0 ? { subCompanyIds: escopo.subCompanyIds } : {}),
    },
  }
}

/** `GET /migration/connections` — conexões salvas (sem senha) e se dá para salvar. */
export const listarConexoesSalvas = async () => {
  const response = await api.get('/migration/connections')
  return { items: response.data.items || [], podeSalvar: Boolean(response.data.podeSalvar) }
}

/**
 * `POST /migration/connections` — salva a conexão para a sincronização usar.
 *
 * O recorte vai junto, e não é detalhe: é ele que a sincronização automática lê
 * para saber de QUEM trazer. Salvar sem recorte deixa a rodada trazendo todas as
 * empresas do banco de origem — inclusive as que a migração acabou de excluir de
 * propósito.
 */
export const salvarConexao = async (nome, conexao, escopo) => {
  const response = await api.post('/migration/connections', {
    ...corpoDaConexao(conexao),
    ...corpoDoEscopo(escopo),
    nome,
  })
  return response.data
}

/** `POST /migration/preview` — simulação: contagens e avisos, sem escrever nada. */
export const simularMigracao = async (conexao, entities, escopo) => {
  const response = await api.post('/migration/preview', {
    ...corpoDaConexao(conexao),
    ...(entities && entities.length > 0 ? { entities } : {}),
    ...corpoDoEscopo(escopo),
  })
  return response.data
}

/** `POST /migration/run` — inicia a carga; responde na hora com o `jobId`. */
export const iniciarMigracao = async (conexao, entities, escopo) => {
  const response = await api.post('/migration/run', {
    ...corpoDaConexao(conexao),
    ...(entities && entities.length > 0 ? { entities } : {}),
    ...corpoDoEscopo(escopo),
  })
  return response.data
}

/** `GET /migration/jobs/:id` — progresso do job em andamento. */
export const consultarJob = async id => {
  const response = await api.get(`/migration/jobs/${id}`)
  return response.data
}

/**
 * `GET /migration/jobs/active` — a migração em andamento, ou `null`.
 *
 * A carga leva minutos e roda no servidor, não na página. Sem esta chamada o
 * `jobId` morre no primeiro F5 e a tela reabre limpa enquanto a migração
 * continua: quem estava acompanhando conclui que ela parou, e a única pista de
 * que não parou é o 409 ao tentar de novo.
 */
export const consultarJobAtivo = async () => {
  const response = await api.get('/migration/jobs/active')
  return response.data.job || null
}

/** `GET /migration/jobs` — histórico das últimas execuções. */
export const listarJobs = async (limit = 20) => {
  const response = await api.get('/migration/jobs', { params: { limit } })
  return response.data.items || []
}
